import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  FormGroup,
  Label,
  Input,
  CardBody,
  Container,
  Button,
  UncontrolledAlert,
  Spinner,
} from "reactstrap";
import Dropzone from "react-dropzone";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const TPRMAnalysis = () => {
  document.title = "TPRM - Vendor Risk Assessment";

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [vendorName, setVendorName] = useState(""); // Vendor Name field
  const [assessmentNotes, setAssessmentNotes] = useState("");
  const [userId] = useState(JSON.parse(localStorage.getItem("authUser"))?.username);

  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertColor, setAlertColor] = useState("success");
  const [loading, setLoading] = useState(false);

  const handleAcceptedFiles = (files) => {
    files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      })
    );
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const removeFile = (index) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updatedFiles);
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("vendorName", vendorName);
    formData.append("assessmentNotes", assessmentNotes);

    selectedFiles.forEach((file) => {
      formData.append("documents", file); // 'documents' key for uploading risk-related files
    });

    try {
      const response = await fetch(`https://api.bluhawk.live/tprm-assessment/${userId}`, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setAlertMessage("Vendor risk assessment submitted successfully!");
        setAlertColor("success");
        setAlertVisible(true);

        setVendorName("");
        setAssessmentNotes("");
        setSelectedFiles([]);

        window.scrollTo({ top: 0, behavior: "smooth" });

        setTimeout(() => {
          setAlertVisible(false);
        }, 5000);
      } else {
        throw new Error("Failed to submit assessment.");
      }
    } catch (error) {
      setAlertMessage("An error occurred while submitting the assessment.");
      setAlertColor("danger");
      setAlertVisible(true);

      setTimeout(() => {
        setAlertVisible(false);
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumb */}
          <Breadcrumbs title="TPRM" breadcrumbItem="Vendor Risk Assessment" />

          {/* Alert */}
          {alertVisible && (
            <UncontrolledAlert
              color={alertColor}
              className="alert-dismissible fade show"
              role="alert"
            >
              {alertMessage}
            </UncontrolledAlert>
          )}

          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Form onSubmit={handleSubmit}>
                    {/* Vendor Name Field */}
                    <FormGroup>
                      <Label for="vendorName">Vendor Name</Label>
                      <Input
                        type="text"
                        id="vendorName"
                        value={vendorName}
                        onChange={(e) => setVendorName(e.target.value)}
                        placeholder="Enter vendor name"
                        required
                      />
                    </FormGroup>

                    {/* Assessment Notes */}
                    <FormGroup>
                      <Label for="assessmentNotes">Assessment Notes</Label>
                      <Input
                        type="textarea"
                        id="assessmentNotes"
                        rows="4"
                        style={{ height: "120px", resize: "none" }}
                        value={assessmentNotes}
                        onChange={(e) => setAssessmentNotes(e.target.value)}
                        placeholder="Add observations, findings, or recommendations..."
                      />
                    </FormGroup>

                    {/* File Upload */}
                    <Dropzone onDrop={(acceptedFiles) => handleAcceptedFiles(acceptedFiles)}>
                      {({ getRootProps, getInputProps }) => (
                        <div className="dropzone">
                          <div
                            className="dz-message needsclick mt-2"
                            {...getRootProps()}
                          >
                            <input {...getInputProps()} />
                            <div className="mb-3">
                              <i className="display-4 text-muted bx bxs-cloud-upload" />
                            </div>
                            <h4>Drop files here or click to upload supporting documents.</h4>
                          </div>
                        </div>
                      )}
                    </Dropzone>

                    <div className="dropzone-previews mt-3">
                      {selectedFiles.map((file, i) => (
                        <Card
                          className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                          key={`${file.name}-${i}`}
                        >
                          <div className="p-2">
                            <Row className="align-items-center">
                              <Col className="col-auto">
                                <img
                                  height="80"
                                  className="avatar-sm rounded bg-light"
                                  alt={file.name}
                                  src={file.preview}
                                />
                              </Col>
                              <Col>
                                <p className="text-muted font-weight-bold mb-1">
                                  {file.name}
                                </p>
                                <p className="mb-0">
                                  <strong>{file.formattedSize}</strong>
                                </p>
                              </Col>
                              <Col className="col-auto">
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => removeFile(i)}
                                >
                                  ✕
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        </Card>
                      ))}
                    </div>

                    {/* Submit Button */}
                    <div className="text-center mt-4">
                      <Button type="submit" color="primary" disabled={loading}>
                        {loading ? <Spinner size="sm" /> : "Submit Assessment"}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TPRMAnalysis;
